import Api from "@/services/api";
import { getCurrentUser } from "@/helpers/helper";
import { loadAccountConfig, PROJECT_TYPE } from "@/config/accounts";
import {
  INIT_PROJECT_CONTEXT,
  RESTORE_PROJECT_CONTEXT,
  VALIDATE_REMAINING_ENTRIES,
  SWITCH_PROJECT,
  SWITCH_ACCOOUNT,
  LOAD_PROJECT,
} from "@/store/_actiontypes";
import {
  SET_PROJECT_ENTRIES,
  SET_ACTIVE_PROJECT,
  SET_INITIALIZED,
  SET_ENTRY_ACCESS,
  SWITCH_SUCCESS,
} from "@/store/_mutationtypes";
import {
  getStoredProjectId,
  setStoredProjectId,
} from "@/helpers/projectStorage";

// Account scoped tokens by account id.
const accountSessions = new Map();

async function switchAccount(dispatch, accountId, accessToken, apply) {
  try {
    const response = await dispatch(
      `account/${SWITCH_ACCOOUNT}`,
      { account_id: accountId, access_token: accessToken, apply },
      { root: true }
    );
    const session = response?.data || null;
    if (session?.access_token) {
      accountSessions.set(accountId, session);
    }
    return session;
  } catch (_) {
    return null;
  }
}

// Makes the token of `accountId` the current session.
function applyAccountSession(commit, accountId) {
  const session = accountSessions.get(accountId);
  if (session?.access_token) {
    commit(`account/${SWITCH_SUCCESS}`, session, { root: true });
  }
  return session || null;
}

// Reads the user role in a project with the given token.
async function fetchProjectRole(projectId, accessToken) {
  if (!projectId || !accessToken) return null;

  try {
    const res = await Api.get(`projects/${projectId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      skipAuthRedirect: true,
    });
    return res?.data?.project_role || null;
  } catch (_) {
    return null;
  }
}

// Validates the user against the account/project list and returns each entry
// with its role and access. Leaves the stored session untouched.
async function validateEntries(dispatch, entries, baseToken, isSessionToken) {
  const accountIds = [...new Set(entries.map((entry) => entry.account_id))];

  // Accounts are independent, so validate them in parallel.
  const tokens = new Map(
    await Promise.all(
      accountIds.map(async (accountId) => [
        accountId,
        (await switchAccount(dispatch, accountId, baseToken, false))
          ?.access_token || null,
      ])
    )
  );

  // Falls back to the session token when no account could be switched into.
  if (isSessionToken && ![...tokens.values()].some(Boolean)) {
    accountIds.forEach((accountId) => tokens.set(accountId, baseToken));
  }

  return Promise.all(
    entries.map(async (entry) => {
      const role = await fetchProjectRole(
        entry.project_id,
        tokens.get(entry.account_id)
      );
      return { ...entry, role, accessible: !!role, validated: true };
    })
  );
}

const state = {
  initialized: false,
  entries: [],
  activeKey: null,
};

const mutations = {
  [SET_PROJECT_ENTRIES](state, entries) {
    state.entries = entries;
  },
  [SET_ACTIVE_PROJECT](state, key) {
    const entry = state.entries.find((item) => item.key === key) || null;
    state.activeKey = entry ? entry.key : null;
    setStoredProjectId(entry ? entry.project_id : null);
  },
  [SET_INITIALIZED](state, initialized) {
    state.initialized = initialized;
  },
  // Records what a role lookup found, from either the background check or
  // LOAD_PROJECT.
  [SET_ENTRY_ACCESS](state, { project_id, role }) {
    state.entries = state.entries.map((entry) =>
      entry.project_id === project_id
        ? { ...entry, role, accessible: !!role, validated: true }
        : entry
    );
  },
};

let initInFlight = null;
let validationInFlight = null;

const actions = {
  // Reopens the project the user was last on, without checking every account
  // first: the stored token already belongs to that project's account, and the
  // caller's LOAD_PROJECT confirms the project itself. Returns null when there
  // is nothing to reopen, so the caller can fall back to the full check.
  async [RESTORE_PROJECT_CONTEXT]({ state, commit, getters }) {
    if (state.initialized) return getters.activeEntry;

    const lastProjectId = getStoredProjectId();
    if (!lastProjectId) return null;

    const allEntries = await loadAccountConfig();
    const lastEntry = allEntries.find(
      (entry) => entry.project_id === lastProjectId
    );
    if (!lastEntry) return null;

    // Access stays unknown until the background check below runs.
    commit(
      SET_PROJECT_ENTRIES,
      allEntries.map((entry) => ({
        ...entry,
        role: null,
        accessible: false,
        validated: false,
      }))
    );
    commit(SET_ACTIVE_PROJECT, lastEntry.key);
    commit(SET_INITIALIZED, true);

    return getters.activeEntry;
  },

  // Works out which of the other projects the user can open. Nothing on screen
  // waits for this, so it runs in the background once the page is up.
  [VALIDATE_REMAINING_ENTRIES]({ state, commit, dispatch }) {
    const unchecked = state.entries.filter((entry) => !entry.validated);
    if (!unchecked.length) return Promise.resolve();
    if (validationInFlight) return validationInFlight;

    validationInFlight = (async () => {
      const baseToken = getCurrentUser()?.access_token || null;
      if (!baseToken) return;

      const checked = await validateEntries(dispatch, unchecked, baseToken, true);
      checked.forEach(({ project_id, role }) =>
        commit(SET_ENTRY_ACCESS, { project_id, role })
      );
    })().finally(() => {
      validationInFlight = null;
    });

    return validationInFlight;
  },

  // Validates the user against the account/project list and activates an
  // accessible entry, the stored one when the route belongs to a project.
  [INIT_PROJECT_CONTEXT](
    { state, commit, getters, dispatch },
    { access_token = null, use_stored_project = false, force = false } = {}
  ) {
    if (state.initialized && !force) return Promise.resolve(getters.activeEntry);
    if (initInFlight) return initInFlight;

    initInFlight = (async () => {
      const configuredEntries = await loadAccountConfig();
      const baseToken = access_token || getCurrentUser()?.access_token || null;

      accountSessions.clear();
      const entries = await validateEntries(
        dispatch,
        configuredEntries,
        baseToken,
        !access_token
      );
      commit(SET_PROJECT_ENTRIES, entries);

      // Digital is the default landing project.
      const storedProjectId = use_stored_project ? getStoredProjectId() : null;
      const accessibleEntries = entries.filter((entry) => entry.accessible);
      const activeEntry =
        accessibleEntries.find(
          (entry) => entry.project_id === storedProjectId
        ) ||
        accessibleEntries.find((entry) => entry.type === PROJECT_TYPE.DIGITAL) ||
        accessibleEntries[0] ||
        null;

      commit(SET_ACTIVE_PROJECT, activeEntry ? activeEntry.key : null);

      if (activeEntry) {
        applyAccountSession(commit, activeEntry.account_id);
      }

      commit(SET_INITIALIZED, true);
      return getters.activeEntry;
    })().finally(() => {
      initInFlight = null;
    });

    return initInFlight;
  },

  // Activates another entry, switching account when it belongs to one.
  async [SWITCH_PROJECT]({ state, commit, getters, dispatch }, key) {
    const entry = state.entries.find((item) => item.key === key);

    if (!entry || !entry.accessible) {
      throw new Error("User has no access to selected project");
    }
    if (entry.key === state.activeKey) return;

    if (entry.account_id !== getters.activeAccountId) {
      const session =
        (await switchAccount(
          dispatch,
          entry.account_id,
          getCurrentUser()?.access_token,
          true
        )) || applyAccountSession(commit, entry.account_id);

      // Activating the entry without its account would leave every request on
      // the previous account's token.
      if (!session?.access_token) {
        throw new Error("Unable to switch to the account of that project");
      }
    }

    commit(SET_ACTIVE_PROJECT, entry.key);

    await dispatch(
      `account/${LOAD_PROJECT}`,
      { project_id: entry.project_id },
      { root: true }
    );
  },
};

const getters = {
  activeProjectKey: (state) => state.activeKey,

  activeEntry: (state) =>
    state.entries.find((entry) => entry.key === state.activeKey) || null,

  activeProjectId: (_state, getters) => getters.activeEntry?.project_id || null,

  activeAccountId: (_state, getters) => getters.activeEntry?.account_id || null,

  activeMaskProfileId: (_state, getters) =>
    getters.activeEntry?.mask_profile_id || null,

  isAnalogProject: (_state, getters) =>
    getters.activeEntry?.type === PROJECT_TYPE.ANALOG,

  accessibleEntries: (state) => state.entries.filter((entry) => entry.accessible),

  // Role already fetched while validating, to avoid requesting it again.
  roleByProjectId: (state) => (projectId) => {
    if (!projectId) return null;
    const match = state.entries.find((entry) => entry.project_id === projectId);
    return match ? match.role : null;
  },

  showProjectSwitcher: (_state, getters) => getters.accessibleEntries.length > 1,

  projectOptions: (_state, getters) =>
    getters.accessibleEntries.map((entry) => ({
      text: entry.label,
      value: entry.key,
    })),
};

export const project = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
